import type { AnalyticsAttributesModel } from '~/common/models/store.index.models.ts';
import type { ApiErrorModel } from '~/common/models/api.models.ts';
import type { PaymentErrorModel, QuizTrackModel } from '~/common/models/mixpanel.model.ts';

export interface ParsedErrorModel {
  message: string
  error_code?: string
  type: string
  stage: string
  gateway: string
}

export function useMixpanel() {
  const route = useRoute();
  const store = useKegelStore();
  const dayjs = useDayjs();
  const { gatewayType } = useHelpers();
  const { $versions, $mixpanel, $paymentMethods, $db, $releaseVersion } = useNuxtApp();

  function parseJSON(string: string) {
    try {
      return JSON.parse(string);
    } catch (error: unknown) {
      console.error(error);
      return { message: string };
    }
  }

  async function getAnalyticsAttributes(clickId?: string): Promise<AnalyticsAttributesModel> {
    const localClickTime = await $db.getItem('b_click_time');
    const savedAttributes = await $db.getItem('attributes');
    const cmpId = await $db.getItem('cmpId');
    const clickTime = (store.clickTime || localClickTime) * 1000;

    return {
      redtrack_click_id: clickId,
      'click_time': clickTime ? dayjs(clickTime).utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null,
      'fbp': useCookie('_fbp').value as string || savedAttributes?.fbp,
      'redtrack_campaign_id': route.query?.cmpid as string || cmpId,
      'campaign': route.query?.sub3 as string || savedAttributes?.campaign,
      'adset': route.query?.sub2 as string || savedAttributes?.adset,
      'ad': route.query?.sub1 as string || savedAttributes?.ad,
      'ad_name': route.query?.sub4 as string || savedAttributes?.ad_name,
      'adset_name': route.query?.sub5 as string || savedAttributes?.adset_name,
      'campaign_name': route.query?.sub6 as string || savedAttributes?.campaign_name,
      'ad_placement': route.query?.sub7 as string || savedAttributes?.ad_placement,
      'project': 'Vue3',
      'fbclid': route.query?.fbclid || savedAttributes?.fbclid,
      'Release version': $releaseVersion,
      utm_source: route.query.utm_source as string,
    };
  }

  function parseError(error: ApiErrorModel | string) {
    const MAGIC_WORD = 'Error message:';
    if (typeof error === 'string') {
      if (error?.includes(MAGIC_WORD)) {
        const [_, partTwo] = error?.split(MAGIC_WORD);
        return { message: partTwo ? MAGIC_WORD + partTwo : error };
      } else {
        return { message: error };
      }
    }
    if (error?.displayMessage) {
      if (error?.displayMessage.includes(MAGIC_WORD)) {
        const [_, partTwo] = error?.displayMessage.split(MAGIC_WORD);
        return { message: partTwo ? MAGIC_WORD + partTwo : error?.displayMessage };
      } else {
        return { message: error?.displayMessage, statusCode: error?.statusCode };
      }
    }
    if (error?.detail?.error) {
      const parsedError = parseJSON(error.detail?.error);
      if (parsedError.message.includes(MAGIC_WORD)) {
        const [_, partTwo] = parsedError.message.split(MAGIC_WORD);
        return { message: partTwo ? MAGIC_WORD + partTwo : parsedError.message };
      } else {
        return parsedError;
      }
    }
    if (error?.detail?.message) {
      return parseError(error.detail?.message);
    } else if (error?.response?.data?.detail?.error) {
      return parseJSON(error?.response?.data?.detail?.error);
    } else {
      return { message: error?.message || error };
    }
  }

  function mixpanelPaymentError({ stage, type, error, errorCode }: PaymentErrorModel): ParsedErrorModel {
    const parsedError = parseError(error);
    delete parsedError.error_msg;
    delete parsedError.http_status_code;
    let gateway = gatewayType.value;

    if (type === $paymentMethods.paypal) {
      gateway = 'PayPal';
    }

    if (errorCode) {
      parsedError.error_code = errorCode;
    }

    mixpanelTrack('Payment Error', {
      ...parsedError,
      type,
      stage,
      gateway,
    });

    return {
      ...parsedError,
      type,
      stage,
      gateway,
    };
  }

  function mixpanelQuizTrack(eventName: string, payload?: QuizTrackModel | AnalyticsAttributesModel): void {
    if ($versions?.value.trackOnlyAfterQuiz !== '0') {
      $mixpanel?.track(eventName, payload);
    }
  }

  function mixpanelTrack(eventName: string, payload?: any): void {
    $mixpanel?.track(eventName, payload);
  }

  async function sendAttribution(
    clickId: string,
    experimentId?: string,
    variation?: string,
  ): Promise<boolean> {
    const savedClickId = await $db.getItem('savedClickId');
    const attributesWasSet = await $db.getItem('attributesWasSet');
    const experimentKey = store.currentExperimentKey || experimentId;
    const experimentVariation = store.currentExperimentVariation || variation;
    const attributes = await getAnalyticsAttributes(clickId);
    if (experimentKey) {
      attributes[experimentKey as keyof AnalyticsAttributesModel] = experimentVariation as string;
    }
    $db?.setItem('attributes', attributes);
    if (clickId !== savedClickId || attributesWasSet !== true) {
      $mixpanel?.people.set(attributes);
      mixpanelTrack('Attribution', attributes as AnalyticsAttributesModel);
      $db?.setItem('savedClickId', clickId || '');
      $db?.setItem('attributesWasSet', true);
    }

    store.analyticsAttributions = attributes;

    return true
  }

  return { sendAttribution, mixpanelPaymentError, mixpanelQuizTrack, mixpanelTrack, getAnalyticsAttributes };
}
